"use client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { isAnalyticsAllowed } from "@/lib/cookies";

function PostHogInit() {
  const initializedRef = useRef(false);
  const identifiedUserRef = useRef<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    function tryInit() {
      if (!isAnalyticsAllowed()) return;

      const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
      const host =
        process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";

      if (!key) return;

      if (!initializedRef.current) {
        posthog.init(key, {
          api_host: host,
          person_profiles: "identified_only",
          capture_pageview: false,
          capture_pageleave: true,
        });
        initializedRef.current = true;
      }

      // Handle user identification and changes (reset on user switch/logout)
      const currentUserId = user?.sub ?? null;
      if (identifiedUserRef.current !== currentUserId) {
        if (identifiedUserRef.current !== null) {
          posthog.reset();
        }
        identifiedUserRef.current = currentUserId;
        if (user?.sub) {
          posthog.identify(user.sub, { email: user.email });
        }
      }
    }

    // Try on mount (consent may already exist)
    tryInit();

    // Listen for consent changes
    const handler = () => tryInit();
    window.addEventListener("cookie-consent-change", handler);
    return () => window.removeEventListener("cookie-consent-change", handler);
  }, [user]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return (
    <PHProvider client={posthog}>
      <PostHogInit />
      {children}
    </PHProvider>
  );
}
